import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import GuestRoute from "@/components/auth/GuestRoute";
import AppShell from "@/components/layout/AppShell";
import ErrorBoundary from "@/components/layout/ErrorBoundary";

const Login = React.lazy(() => import("@/pages/Login"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const HoldBills = React.lazy(() => import("@/pages/HoldBills"));
const ProductCategories = React.lazy(() => import("@/pages/masters/ProductCategories"));
const Products = React.lazy(() => import("@/pages/masters/Products"));
const CustomerMaster = React.lazy(() => import("@/pages/masters/CustomerMaster"));
const SupplierMaster = React.lazy(() => import("@/pages/masters/SupplierMaster"));
const ScheduleMaster = React.lazy(() => import("@/pages/accounts/ScheduleMaster"));
const AccountMaster = React.lazy(() => import("@/pages/accounts/AccountMaster"));
const ReceiptVoucher = React.lazy(() => import("@/pages/accounts/ReceiptVoucher"));
const PaymentVoucher = React.lazy(() => import("@/pages/accounts/PaymentVoucher"));
const JournalVoucher = React.lazy(() => import("@/pages/accounts/JournalVoucher"));
const ContraVoucher = React.lazy(() => import("@/pages/accounts/ContraVoucher"));
const DebitNoteVoucher = React.lazy(() => import("@/pages/accounts/DebitNoteVoucher"));
const CreditNoteVoucher = React.lazy(() => import("@/pages/accounts/CreditNoteVoucher"));
const LedgerReport = React.lazy(() => import("@/pages/accounts/LedgerReport"));
const TrialBalanceReport = React.lazy(() => import("@/pages/accounts/TrialBalanceReport"));
const ProfitLossReport = React.lazy(() => import("@/pages/accounts/ProfitLossReport"));
const BalanceSheetReport = React.lazy(() => import("@/pages/accounts/BalanceSheetReport"));
const AccountBooks = React.lazy(() => import("@/pages/accounts/AccountBooks"));
const AccountingSettings = React.lazy(() => import("@/pages/accounts/AccountingSettings"));
const ReportCenterLayout = React.lazy(() => import("@/components/reports/ReportCenterLayout"));
const ReportCenterHome = React.lazy(() => import("@/pages/reports/ReportCenterHome"));
const ReportPlaceholder = React.lazy(() => import("@/pages/reports/ReportPlaceholder"));
const SalesReports = React.lazy(() => import("@/pages/reports/SalesReports"));
const PosRoundOffReports = React.lazy(() => import("@/pages/reports/PosRoundOffReports"));
const PurchaseReports = React.lazy(() => import("@/pages/reports/PurchaseReports"));
const CashReports = React.lazy(() => import("@/pages/reports/CashReports"));
const OperationsReports = React.lazy(() => import("@/pages/reports/OperationsReports"));
const StockReports = React.lazy(() => import("@/pages/reports/StockReports"));
const ExpiryReport = React.lazy(() => import("@/pages/reports/ExpiryReport"));
const FinancialReports = React.lazy(() => import("@/pages/reports/FinancialReports"));
const OutstandingReports = React.lazy(() => import("@/pages/reports/OutstandingReports"));
const GroupSummaryReports = React.lazy(() => import("@/pages/reports/GroupSummaryReports"));
const VatReports = React.lazy(() => import("@/pages/reports/VatReports"));
const DeliveryReports = React.lazy(() => import("@/pages/reports/DeliveryReports"));
const ProfitReports = React.lazy(() => import("@/pages/reports/ProfitReports"));
const PosActionsReport = React.lazy(() => import("@/pages/reports/PosActionsReport"));
const InvoiceCreditReport = React.lazy(() => import("@/pages/reports/InvoiceCreditReport"));
const OutdoorSalesmanProfitReport = React.lazy(() => import("@/pages/reports/OutdoorSalesmanProfitReport"));
const BacklogReports = React.lazy(() => import("@/pages/reports/BacklogReports"));
const UsersPage = React.lazy(() => import("@/pages/admin/Users"));
const RolesPage = React.lazy(() => import("@/pages/admin/Roles"));
const AuditLogPage = React.lazy(() => import("@/pages/admin/AuditLog"));
const CountersPage = React.lazy(() => import("@/pages/admin/Counters"));
const PrintersPage = React.lazy(() => import("@/pages/admin/Printers"));
const CompanySetup = React.lazy(() => import("@/pages/admin/CompanySetup"));
const StoreSetup = React.lazy(() => import("@/pages/admin/StoreSetup"));
const TaxSettings = React.lazy(() => import("@/pages/admin/TaxSettings"));
const GeneralSettings = React.lazy(() => import("@/pages/admin/GeneralSettings"));
const WhatsAppSettings = React.lazy(() => import("@/pages/admin/WhatsAppSettings"));
const InventoryHubPage = React.lazy(() => import("@/pages/inventory/InventoryHub"));
const StockSummary = React.lazy(() => import("@/pages/inventory/StockSummary"));
const PurchaseOrders = React.lazy(() => import("@/pages/inventory/PurchaseOrders"));
const GoodsReceipts = React.lazy(() => import("@/pages/inventory/GoodsReceipts"));
const Transfers = React.lazy(() => import("@/pages/inventory/Transfers"));
const Adjustments = React.lazy(() => import("@/pages/inventory/Adjustments"));
const OpeningPhysical = React.lazy(() => import("@/pages/inventory/OpeningPhysical"));
const Wastage = React.lazy(() => import("@/pages/inventory/Wastage"));
const SupplierReturns = React.lazy(() => import("@/pages/inventory/SupplierReturns"));
const InventoryAlerts = React.lazy(() => import("@/pages/inventory/InventoryAlerts"));
const TaxInvoice = React.lazy(() => import("@/pages/pos/TaxInvoice"));
const InvoiceHistory = React.lazy(() => import("@/pages/pos/InvoiceHistory"));
const SalesReturns = React.lazy(() => import("@/pages/pos/SalesReturns"));
const Quotations = React.lazy(() => import("@/pages/pos/Quotations"));
const BranchComparisonReport = React.lazy(() => import("@/pages/reports/BranchComparisonReport"));
const SupplierComplianceReport = React.lazy(() => import("@/pages/reports/SupplierComplianceReport"));
const StoreHome = React.lazy(() => import("@/pages/store/StoreHome"));
const StoreCatalog = React.lazy(() => import("@/pages/store/StoreCatalog"));
const StoreCheckout = React.lazy(() => import("@/pages/store/StoreCheckout"));
const OnlineOrders = React.lazy(() => import("@/pages/online/OnlineOrders"));
const OnlineOrderHistory = React.lazy(() => import("@/pages/online/OnlineOrderHistory"));
const OnlineStoreSettings = React.lazy(() => import("@/pages/admin/OnlineStoreSettings"));
const OnlineOrdersReport = React.lazy(() => import("@/pages/reports/OnlineOrdersReport"));
const DiscountReport = React.lazy(() => import("@/pages/reports/DiscountReport"));
const RxPrintLogReport = React.lazy(() => import("@/pages/reports/RxPrintLogReport"));
const BrandMaster = React.lazy(() => import("@/pages/masters/BrandMaster"));
const ShelfMaster = React.lazy(() => import("@/pages/masters/ShelfMaster"));
const AreaMaster = React.lazy(() => import("@/pages/masters/AreaMaster"));
const UnitCatalog = React.lazy(() => import("@/pages/masters/UnitCatalog"));
const PriceChangeRequests = React.lazy(() => import("@/pages/masters/PriceChangeRequests"));
const BarcodeLabels = React.lazy(() => import("@/pages/masters/BarcodeLabels"));
const PrivilegeCardMaster = React.lazy(() => import("@/pages/masters/PrivilegeCardMaster"));
const PurchaseRequests = React.lazy(() => import("@/pages/inventory/PurchaseRequests"));
const PurchaseDebitNotes = React.lazy(() => import("@/pages/inventory/PurchaseDebitNotes"));
const DenominationMaster = React.lazy(() => import("@/pages/admin/DenominationMaster"));
const MisReports = React.lazy(() => import("@/pages/reports/MisReports"));

const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, "") || undefined;

function GuestFallback() {
  return (
    <div className="min-h-screen grid place-items-center bg-brand-surface">
      <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
    </div>
  );
}

function LegacyPulseRedirect({ to = "/" }) {
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <BrowserRouter basename={routerBasename}>
        <Routes>
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Suspense fallback={<GuestFallback />}>
                  <Login />
                </Suspense>
              </GuestRoute>
            }
          />

          <Route
            path="/s/:branchCode"
            element={
              <Suspense fallback={<GuestFallback />}>
                <StoreHome />
              </Suspense>
            }
          />
          <Route
            path="/s/:branchCode/catalog"
            element={
              <Suspense fallback={<GuestFallback />}>
                <StoreCatalog />
              </Suspense>
            }
          />
          <Route
            path="/s/:branchCode/checkout"
            element={
              <Suspense fallback={<GuestFallback />}>
                <StoreCheckout />
              </Suspense>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/pos" element={<TaxInvoice />} />
            <Route path="/pos/invoice-history" element={<InvoiceHistory />} />
            <Route path="/pos/held-bills" element={<HoldBills />} />
            <Route path="/pos/sales-returns" element={<SalesReturns />} />
            <Route path="/pos/quotations" element={<Quotations />} />

            {/* Retired grocery / restaurant / Pulse routes → Lumina Tax Invoice */}
            <Route path="/waiter/*" element={<LegacyPulseRedirect to="/pos" />} />
            <Route path="/kds/*" element={<LegacyPulseRedirect to="/" />} />
            <Route path="/tables/*" element={<LegacyPulseRedirect to="/pos" />} />
            <Route path="/masters/promotions/*" element={<LegacyPulseRedirect to="/masters/products" />} />
            <Route path="/masters/promotion-rules/*" element={<LegacyPulseRedirect to="/masters/products" />} />
            <Route path="/inventory/batches/*" element={<LegacyPulseRedirect to="/inventory/stock" />} />
            <Route path="/inventory/wrapping/*" element={<LegacyPulseRedirect to="/inventory" />} />
            <Route path="/inventory/pdt/*" element={<LegacyPulseRedirect to="/inventory" />} />
            <Route path="/inventory/job-work/*" element={<LegacyPulseRedirect to="/inventory" />} />
            <Route path="/admin/loyalty/*" element={<LegacyPulseRedirect to="/admin/general" />} />
            <Route path="/admin/weighing-machines/*" element={<LegacyPulseRedirect to="/admin/general" />} />
            <Route path="/admin/aggregators/*" element={<LegacyPulseRedirect to="/admin/online-store" />} />
            <Route path="/admin/restaurant/*" element={<LegacyPulseRedirect to="/admin/store" />} />
            <Route path="/masters/kitchen-notes/*" element={<LegacyPulseRedirect to="/masters/products" />} />
            <Route path="/masters/menu-items/*" element={<LegacyPulseRedirect to="/masters/products" />} />

            <Route path="/reports/kitchen/*" element={<LegacyPulseRedirect to="/reports/top-items" />} />

            <Route path="/online/orders" element={<OnlineOrders />} />
            <Route path="/online/history" element={<OnlineOrderHistory />} />

            <Route path="/masters/categories/*" element={<ProductCategories />} />
            <Route path="/masters/products/*" element={<Products />} />
            <Route path="/masters/brands/*" element={<BrandMaster />} />
            <Route path="/masters/shelves/*" element={<ShelfMaster />} />
            <Route path="/masters/areas/*" element={<AreaMaster />} />
            <Route path="/masters/units" element={<UnitCatalog />} />
            <Route path="/masters/price-changes" element={<PriceChangeRequests />} />
            <Route path="/masters/barcode-labels" element={<BarcodeLabels />} />
            <Route path="/masters/privilege-cards/*" element={<PrivilegeCardMaster />} />
            <Route path="/masters/customers" element={<CustomerMaster />} />
            <Route path="/masters/suppliers" element={<SupplierMaster />} />

            <Route path="/inventory" element={<InventoryHubPage />} />
            <Route path="/inventory/stock" element={<StockSummary />} />
            <Route path="/inventory/purchase-requests" element={<PurchaseRequests />} />
            <Route path="/inventory/purchase-orders" element={<PurchaseOrders />} />
            <Route path="/inventory/grn/*" element={<GoodsReceipts />} />
            <Route path="/inventory/purchase-debit-notes" element={<PurchaseDebitNotes />} />
            <Route path="/inventory/transfers" element={<Transfers />} />
            <Route path="/inventory/adjustments" element={<Adjustments />} />
            <Route path="/inventory/opening" element={<OpeningPhysical />} />
            <Route path="/inventory/wastage" element={<Wastage />} />
            <Route path="/inventory/returns" element={<SupplierReturns />} />
            <Route path="/inventory/alerts" element={<InventoryAlerts />} />

            <Route path="/accounts/schedules" element={<ScheduleMaster />} />
            <Route path="/accounts/accounts" element={<AccountMaster />} />
            <Route path="/accounts/receipt" element={<ReceiptVoucher />} />
            <Route path="/accounts/payment" element={<PaymentVoucher />} />
            <Route path="/accounts/journal" element={<JournalVoucher />} />
            <Route path="/accounts/contra" element={<ContraVoucher />} />
            <Route path="/accounts/debit-note" element={<DebitNoteVoucher />} />
            <Route path="/accounts/credit-note" element={<CreditNoteVoucher />} />
            <Route path="/accounts/ledger" element={<LedgerReport />} />
            <Route path="/accounts/trial-balance" element={<TrialBalanceReport />} />
            <Route path="/accounts/profit-loss" element={<ProfitLossReport />} />
            <Route path="/accounts/balance-sheet" element={<BalanceSheetReport />} />
            <Route path="/accounts/cash-book" element={<AccountBooks />} />
            <Route path="/accounts/bank-book" element={<AccountBooks />} />
            <Route path="/accounts/day-book" element={<AccountBooks />} />
            <Route path="/accounts/settings" element={<AccountingSettings />} />

            <Route path="/reports" element={<ReportCenterLayout />}>
              <Route index element={<ReportCenterHome />} />
              <Route path="daily-sales" element={<SalesReports />} />
              <Route path="counter-sales" element={<SalesReports />} />
              <Route path="item-sales" element={<SalesReports />} />
              <Route path="tax" element={<SalesReports />} />
              <Route path="vat-computing" element={<VatReports />} />
              <Route path="input-vat" element={<VatReports />} />
              <Route path="output-vat" element={<VatReports />} />
              <Route path="category-sales" element={<SalesReports />} />
              <Route path="dept-sales" element={<SalesReports />} />
              <Route path="hourly-sales" element={<SalesReports />} />
              <Route path="area-sales-summary" element={<SalesReports />} />
              <Route path="area-sales-invoice" element={<SalesReports />} />
              <Route path="credit-sales" element={<SalesReports />} />
              <Route path="invoice-credit" element={<InvoiceCreditReport />} />
              <Route path="outdoor-salesman-profit" element={<OutdoorSalesmanProfitReport />} />
              <Route path="sales-day-book-denom" element={<SalesReports />} />
              <Route path="pos-round-bill" element={<PosRoundOffReports />} />
              <Route path="pos-round-date" element={<PosRoundOffReports />} />
              <Route path="pos-actions" element={<PosActionsReport />} />
              <Route path="pos-actions" element={<PosActionsReport />} />
              <Route path="purchase-day-book" element={<PurchaseReports />} />
              <Route path="product-purchase-history" element={<PurchaseReports />} />
              <Route path="purchase-summary" element={<PurchaseReports />} />
              <Route path="product-purchase-period" element={<PurchaseReports />} />
              <Route path="product-purchase-details" element={<PurchaseReports />} />
              <Route path="bill-purchase" element={<PurchaseReports />} />
              <Route path="purchase-general" element={<PurchaseReports />} />
              <Route path="area-purchase-summary" element={<PurchaseReports />} />
              <Route path="area-purchase-invoice" element={<PurchaseReports />} />
              <Route path="debit-note-report" element={<PurchaseReports />} />
              <Route path="purchase-volume" element={<PurchaseReports />} />
              <Route path="purchase-and-return" element={<PurchaseReports />} />
              <Route path="purchase-return-register" element={<PurchaseReports />} />
              <Route path="bill-purchase-return" element={<PurchaseReports />} />
              <Route path="cash-collection" element={<CashReports />} />
              <Route path="z-report" element={<CashReports />} />
              <Route path="x-report" element={<CashReports />} />
              <Route path="sales-day-book" element={<CashReports />} />
              <Route path="customer-sales" element={<OperationsReports />} />
              <Route path="delivery-area-sales" element={<OperationsReports />} />
              <Route path="delivery-person-sales" element={<DeliveryReports />} />
              <Route path="delivery-person-detail" element={<DeliveryReports />} />
              <Route path="delivery-customer" element={<DeliveryReports />} />
              <Route path="condensed-stock" element={<StockReports />} />
              <Route path="condensed-physical" element={<StockReports />} />
              <Route path="opening-stock" element={<StockReports />} />
              <Route path="physical-stock" element={<StockReports />} />
              <Route path="stock-adjustment" element={<StockReports />} />
              <Route path="reorder-level" element={<StockReports />} />
              <Route path="stock-transfer" element={<StockReports />} />
              <Route path="batch-expiry" element={<ExpiryReport />} />
              <Route path="financial-statements" element={<FinancialReports />} />
              <Route path="customer-age-wise" element={<OutstandingReports />} />
              <Route path="supplier-age-wise" element={<FinancialReports />} />
              <Route path="bills-receivable" element={<OutstandingReports />} />
              <Route path="receivable-last" element={<OutstandingReports />} />
              <Route path="company-outstanding" element={<OutstandingReports />} />
              <Route path="short-term-credit" element={<OutstandingReports />} />
              <Route path="promised-receivable" element={<OutstandingReports />} />
              <Route path="customer-ledger" element={<FinancialReports />} />
              <Route path="supplier-ledger" element={<FinancialReports />} />
              <Route path="top-items" element={<BacklogReports />} />
              <Route path="supplier-summary" element={<BacklogReports />} />
              <Route path="order-summary" element={<BacklogReports />} />
              <Route path="branch-comparison" element={<BranchComparisonReport />} />
              <Route path="supplier-compliance" element={<SupplierComplianceReport />} />
              <Route path="online-orders" element={<OnlineOrdersReport />} />
              <Route path="discounts" element={<DiscountReport />} />
              <Route path="rx-print-log" element={<RxPrintLogReport />} />
              <Route path="profit-dept" element={<ProfitReports />} />
              <Route path="profit-cat" element={<ProfitReports />} />
              <Route path="profit-subcat" element={<ProfitReports />} />
              <Route path="profit-details" element={<ProfitReports />} />
              <Route path="mis" element={<MisReports />} />
              <Route path="ledger" element={<LedgerReport />} />
              <Route path="group-ledger" element={<GroupSummaryReports />} />
              <Route path="cash-group" element={<GroupSummaryReports />} />
              <Route path="bank-group" element={<GroupSummaryReports />} />
              <Route path="trial-balance" element={<TrialBalanceReport />} />
              <Route path="profit-loss" element={<ProfitLossReport />} />
              <Route path="balance-sheet" element={<BalanceSheetReport />} />
              <Route path="cash-book" element={<AccountBooks />} />
              <Route path="bank-book" element={<AccountBooks />} />
              <Route path="day-book" element={<AccountBooks />} />
              <Route path=":reportId" element={<ReportPlaceholder />} />
            </Route>

            <Route path="/admin/users/*" element={<UsersPage />} />
            <Route path="/admin/roles/*" element={<RolesPage />} />
            <Route path="/admin/audit-log" element={<AuditLogPage />} />
            <Route path="/admin/counters" element={<CountersPage />} />
            <Route path="/admin/printers/*" element={<PrintersPage />} />
            <Route path="/admin/denominations/*" element={<DenominationMaster />} />
            <Route path="/admin/company" element={<CompanySetup />} />
            <Route path="/admin/store" element={<StoreSetup />} />
            <Route path="/admin/tax" element={<TaxSettings />} />
            <Route path="/admin/general" element={<GeneralSettings />} />
            <Route path="/admin/whatsapp" element={<WhatsAppSettings />} />
            <Route path="/admin/online-store" element={<OnlineStoreSettings />} />
            <Route path="/reports/online-orders" element={<OnlineOrdersReport />} />
            <Route path="/reports/mis" element={<MisReports />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
      <Toaster richColors closeButton duration={4000} position="bottom-right" />
    </div>
  );
}

export default App;
